import axios, { AxiosRequestConfig } from 'axios'
import store from '@/store'
import router from '@/router'
import Chart from 'chart.js'
import type { CheckLogin, Logout, NavItem, NavResponse, Page, PageResponse, PageUpdateResponse, PagesResponse, Color, UIColor, ColorSchemeResponse, FontsResponse, SocialMediaSettings, GlobalSettings, GlobalSettingsResponse, AnalyticsData, AnalyticsAPIResponse, RightBar, RightBarGlobalSettings, RightBarResponse, DomainTipResponse, DomainImagesResponse, Snapshot, SnapshotList, SecondaryDomainsList, MessageResponse, DomainEmailsResponse, LoginLinkResponse, CreateMailBoxResponse, RobotsTxtResponse, LlmsTextResponse, AiSeoData, AiSeoPrefill, AiSeoSaveResult, AiSeoImagesResponse, CookiePolicyLink } from '@/interfaces'
import moment from 'moment'

export class Api {
  static async checkLogin(email: string, password: string, rememberMe: boolean, recaptchaToken: string, domain: string): Promise<CheckLogin> {
    const apiResult = await this.makeRequest<CheckLogin>({
      method: 'post',
      url: '/login',
      data: {
        email: email,
        password: password,
        rememberMe: rememberMe,
        domain: domain,
        recaptchaToken: recaptchaToken,
      },
    })

    return apiResult
  }

  static async forgotPassword(email: string, recaptchaToken: string, domain: string): Promise<MessageResponse> {
    const apiResult = await this.makeRequest<MessageResponse>({
      method: 'post',
      url: '/forgot-password',
      data: {
        email: email,
        recaptchaToken: recaptchaToken,
        domain: domain,
      },
    })

    return apiResult
  }

  static async resetPassword(password: string, confirm_password: string, recaptchaToken: string, domain: string, resetToken: string): Promise<MessageResponse> {
    const apiResult = await this.makeRequest<MessageResponse>({
      method: 'post',
      url: '/reset-password',
      data: {
        password: password,
        password_confirmation: confirm_password,
        recaptchaToken: recaptchaToken,
        domain: domain,
        resetToken: resetToken,
      },
    })

    return apiResult
  }

  static async logout(): Promise<Logout> {
    const apiResult = await this.makeRequest<Logout>({
      method: 'post',
      url: '/logout',
    })

    return apiResult
  }

  static async getDomainNav(): Promise<NavResponse> {
    const apiResult = await this.makeRequest<NavResponse>({
      method: 'get',
      url: '/domain/nav',
    })

    return apiResult
  }

  static async setDomainNav(nav: NavItem[]): Promise<NavItem[]> {
    const apiResult = await this.makeRequest<NavResponse>({
      method: 'put',
      url: '/domain/nav',
      data: nav,
    })

    return apiResult.nav
  }

  static async getDomainPage(pageid: number): Promise<Page> {
    const apiResult = await this.makeRequest<PageResponse>({
      method: 'get',
      url: '/domain/page/' + pageid,
    })

    return apiResult.page
  }

  static async getDomainPages(): Promise<Page[]> {
    const apiResult = await this.makeRequest<PagesResponse>({
      method: 'get',
      url: '/domain/pages',
    })

    return apiResult.pages
  }

  static async updateDomainPage(page: Page): Promise<number> {
    const apiResult = await this.makeRequest<PageUpdateResponse>({
      method: 'put',
      url: '/domain/page/' + page.id,
      data: {
        title: page.title,
        content: page.content,
        heroImages: page.heroImages,
      },
    })

    return apiResult.rows_affected
  }

  // static async getDomainColorScheme(): Promise<Color[]> {
  static async getDomainColorScheme(): Promise<ColorSchemeResponse> {
    const apiResult = await this.makeRequest<ColorSchemeResponse>({
      method: 'get',
      url: '/domain/colorscheme/',
    })

    // return apiResult.scheme
    return apiResult
  }

  static async setDomainColorScheme(colors: UIColor[]): Promise<Color[]> {
    const scheme = []
    for (const color of colors) {
      scheme.push({
        name: color.name,
        hex: color.hex,
      })
    }
    const apiResult = await this.makeRequest<ColorSchemeResponse>({
      method: 'put',
      url: '/domain/colorscheme/',
      data: {
        scheme: scheme,
      },
    })

    return apiResult.scheme
  }

  static async getDomainFonts(): Promise<FontsResponse> {
    const apiResult = await this.makeRequest<FontsResponse>({
      method: 'get',
      url: '/domain/fonts/',
    })

    return apiResult
  }

  static async setDomainFonts(userFontsBody: string, userFontsContent: string, userFontsFeatured: string): Promise<FontsResponse> {
    const apiResult = await this.makeRequest<FontsResponse>({
      method: 'put',
      url: '/domain/fonts/',
      data: {
        userFontsBody: userFontsBody,
        userFontsContent: userFontsContent,
        userFontsFeatured: userFontsFeatured,
      },
    })

    return apiResult
  }

  static async getSocialMediaSettings(): Promise<SocialMediaSettings> {
    const apiResult = await this.makeRequest<SocialMediaSettings>({
      method: 'get',
      url: '/domain/socialmedia/',
    })

    return apiResult
  }

  static async setSocialMediaSettings(settings: SocialMediaSettings): Promise<SocialMediaSettings> {
    const apiResult = await this.makeRequest<SocialMediaSettings>({
      method: 'put',
      url: '/domain/socialmedia/',
      data: {
        settings: settings,
      },
    })

    return apiResult
  }

  static async getGlobalSettings(): Promise<GlobalSettingsResponse> {
    const apiResult = await this.makeRequest<GlobalSettingsResponse>({
      method: 'get',
      url: '/domain/globalsettings/',
    })

    return apiResult
  }

  static async setGlobalSettings(settings: GlobalSettings): Promise<GlobalSettings> {
    const apiResult = await this.makeRequest<GlobalSettings>({
      method: 'put',
      url: '/domain/globalsettings/',
      data: {
        settings: settings,
      },
    })

    return apiResult
  }

  static async getAnalyticsData(start_at: number, end_at: number, unit: string, url: string = null): Promise<AnalyticsAPIResponse> {
    const apiResult = await this.makeRequest<AnalyticsData>({
      method: 'post',
      url: '/domain/analytics/',
      data: {
        start_at: start_at,
        end_at: end_at,
        unit: unit,
        tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
        url: url,
      },
    })

    // generate intervals and move pageviews to a chart structure
    const labels = []
    const data = []
    const stepPoint = moment(start_at)
    while (stepPoint < moment(end_at)) {
      let format = ''
      // format depending on a unit
      switch (unit) {
        case 'hour':
          format = 'ddd HH A'
          break
        case 'day':
          format = 'ddd, MMM DD'
          break
        case 'month':
          format = 'MMM YYYY'
          break
        default:
          format = 'DD-MM'
          break
      }
      const label = stepPoint.format(format)
      labels.push(label)
      let views = 0
      for (const pageview of apiResult.pageviews) {
        const point = moment(pageview.t)
        if (label === point.format(format)) {
          views = pageview.y
        }
      }
      data.push(views)

      // add to a date depending on unit
      switch (unit) {
        case 'hour':
          stepPoint.add(1, 'hours')
          break
        case 'day':
          stepPoint.add(1, 'days')
          break
        case 'month':
          stepPoint.add(1, 'months')
          break
        default:
          stepPoint.add(1, 'days')
          break
      }
    }

    const pageviewsChart = {
      labels: labels,
      datasets: [
        {
          label: 'Page Views',
          data: data,
          backgroundColor: '#717aed',
        },
      ],
    }

    return {
      pageviews: pageviewsChart,
      sessions: apiResult.sessions,
      urlMetrics: apiResult.urlMetrics,
      deviceMetrics: apiResult.deviceMetrics,
    }
  }

  static async getDomainRightBar(): Promise<RightBarResponse> {
    const apiResult = await this.makeRequest<RightBarResponse>({
      method: 'get',
      url: '/domain/rightbar',
    })

    return apiResult
  }

  static async setDomainRightBar(dynamicBlocks: RightBar[], rightBarGlobalSettings: RightBarGlobalSettings) {
    const apiResult = await this.makeRequest<RightBarResponse>({
      method: 'post',
      url: '/domain/rightbar',
      data: {
        dynamicBlocks: dynamicBlocks,
        rightBarGlobalSettings: rightBarGlobalSettings,
      },
    })

    return apiResult
  }

  static async getDashboardTip(): Promise<DomainTipResponse> {
    const apiResult = await this.makeRequest<DomainTipResponse>({
      method: 'get',
      url: '/dashboardtip',
    })

    return apiResult
  }

  static async getDomainImages(): Promise<DomainImagesResponse> {
    const apiResult = await this.makeRequest<DomainImagesResponse>({
      method: 'get',
      url: '/domain/images',
    })

    return apiResult
  }

  static async deleteDomainImage(imageId) {
    const apiResult = await this.makeRequest({
      method: 'post',
      url: '/domain/deleteimage',
      data: {
        imageId: imageId,
      },
    })

    return apiResult
  }

  static async fetchSnapshotList() {
    const apiResult = await this.makeRequest<SnapshotList>({
      method: 'get',
      url: '/domain/snapshotlist',
    })

    return apiResult
  }

  static async restoreWebsiteContent(snapshotDate) {
    const apiResult = await this.makeRequest({
      method: 'post',
      url: '/domain/restorewebsitecontent',
      data: {
        snapshotDate: snapshotDate,
      },
    })

    return apiResult
  }

  static async fetchDomainEmails(domain: string): Promise<DomainEmailsResponse> {
    const apiResult = await this.makeRequest<DomainEmailsResponse>({
      method: 'post',
      url: '/domain/getdomainemails',
      data: {
        servicedomain: domain,
      },
    })

    return apiResult
  }

  static async getLoginLink(domain: string, loginEmail: string): Promise<LoginLinkResponse> {
    const apiResult = await this.makeRequest<LoginLinkResponse>({
      method: 'post',
      url: '/domain/getemailaccountloginlink',
      data: {
        servicedomain: domain,
        loginEmail: loginEmail,
      },
    })

    return apiResult
  }

  static async createNewMailBox(domain: string, emailAddress: string, password: string, password_confirmation: string): Promise<CreateMailBoxResponse> {
    const apiResult = await this.makeRequest<CreateMailBoxResponse>({
      method: 'post',
      url: '/domain/createnewmailbox',
      data: {
        servicedomain: domain,
        emailAddress: emailAddress,
        password: password,
        password_confirmation: password_confirmation,
      },
    })

    return apiResult
  }

  static async resetUserPassword(password: string, confirm_password: string, domain: string) {
    const apiResult = await this.makeRequest({
      method: 'post',
      url: '/reset-user-password',
      data: {
        password: password,
        password_confirmation: confirm_password,
        domain: domain,
      },
    })

    return apiResult
  }

  static async fetchSecondaryDomainsList(domain?: string): Promise<SecondaryDomainsList> {
    const apiResult = await this.makeRequest<SecondaryDomainsList>({
      method: 'post',
      url: '/fetchSecondaryDomains',
      data: {
        domain: domain,
      },
    })

    return apiResult
  }

  static async deleteSecondaryDomain(secondaryDomain: string) {
    const apiResult = await this.makeRequest({
      method: 'post',
      url: '/deleteSecondaryDomain',
      data: {
        secondaryDomain: secondaryDomain,
      },
    })

    return apiResult
  }

  static async addSecondaryDomain(secondaryDomain: string) {
    const apiResult = await this.makeRequest({
      method: 'post',
      url: '/addSecondaryDomain',
      data: {
        secondaryDomain: secondaryDomain,
      },
    })

    return apiResult
  }

  static async cloneSite(domain: string): Promise<MessageResponse> {
    const apiResult = await this.makeRequest<MessageResponse>({
      method: 'post',
      url: '/cloneSite',
      data: {
        domain: domain,
      },
    })

    return apiResult
  }

  static async getDomainRobotsTxt(domain: any): Promise<RobotsTxtResponse> {
    const apiResult = await this.makeRequest<RobotsTxtResponse>({
      method: 'get',
      url: '/domain/robots',
      data: {
        domain: domain,
      },
    })

    return apiResult
  }

  static async updateDomainRobotsTxt(domain: string, robots_content: string) {
    const apiResult = await this.makeRequest({
      method: 'post',
      url: '/domain/robots',
      data: {
        domain: domain,
        robots_content: robots_content,
      },
    })

    return apiResult
  }

  static async getDomainLlmsText(domain: any): Promise<LlmsTextResponse> {
    const apiResult = await this.makeRequest<LlmsTextResponse>({
      method: 'get',
      url: '/domain/llms-text',
      data: {
        domain: domain,
      },
    })

    return apiResult
  }

  static async updateDomainLlmsText(domain: string, llms_content: string) {
    const apiResult = await this.makeRequest({
      method: 'post',
      url: '/domain/llms-text',
      data: {
        domain: domain,
        llms_content: llms_content,
      },
    })

    return apiResult
  }

  static async getAiSeo(): Promise<AiSeoData | AiSeoPrefill> {
    return this.makeRequest({ method: 'get', url: '/ai-seo' })
  }

  static async saveAiSeo(payload: object): Promise<AiSeoSaveResult> {
    try {
      const response = await axios({
        method: 'post',
        url: '/ai-seo',
        headers: { Authorization: `Bearer ${this.getBearerToken()}` },
        data: payload,
      })
      return { data: response.data.data }
    } catch (error) {
      if (error.response && error.response.status === 422) {
        return { errors: error.response.data.errors, message: error.response.data.message }
      }
      throw error
    }
  }

  static async deleteAiSeo(): Promise<MessageResponse> {
    return this.makeRequest({ method: 'delete', url: '/ai-seo' })
  }

  static async getAiSeoImages(): Promise<AiSeoImagesResponse> {
    return this.makeRequest({ method: 'get', url: '/ai-seo/images' })
  }

  static async getJsEmbeds(): Promise<{ data: any[] }> {
    return this.makeRequest({ method: 'get', url: '/js-embeds' })
  }

  static async saveJsEmbed(snippet: string, includeInHead = false): Promise<any> {
    return this.makeRequest({ method: 'post', url: '/js-embeds', data: { snippet, include_in_head: includeInHead } })
  }

  static async updateJsEmbed(id: number, includeInHead: boolean): Promise<MessageResponse> {
    return this.makeRequest({ method: 'patch', url: `/js-embeds/${id}`, data: { include_in_head: includeInHead } })
  }

  static async deleteJsEmbed(id: number): Promise<MessageResponse> {
    return this.makeRequest({ method: 'delete', url: `/js-embeds/${id}` })
  }

  static async getCookiePolicyLinks(): Promise<{ data: CookiePolicyLink[] }> {
    return this.makeRequest({ method: 'get', url: '/cookie-policy-links' })
  }

  static async saveCookiePolicyLinks(links: CookiePolicyLink[]): Promise<{ data: CookiePolicyLink[] }> {
    return this.makeRequest({ method: 'put', url: '/cookie-policy-links', data: { links } })
  }

  private static async makeRequest<T>(config: AxiosRequestConfig): Promise<T> {
    axios.defaults.baseURL = this.getBaseUrl()
    axios.defaults.headers.common.Authorization = `Bearer ${this.getBearerToken()}`
    const response = await axios(config).then(function (response) {
      return response.data as T
    }).catch(function (error) {
      // handle unauthorized requests
      if (error.response.status === 403) {
        store.commit('logout')
        if (router.currentRoute.path !== '/login') {
          router.push({ path: '/login' })
        }
        throw new Error('Your session has expired. Please login again to continue!')
      }
      if ('data' in error.response) {
        let errorString = '<ul>'
        for (const fieldKey in error.response.data) {
          errorString += '<ul>'
          for (const errorMessageKey in error.response.data[fieldKey]) {
            errorString += '<li>' + error.response.data[fieldKey][errorMessageKey] + '</li>'
          }
          errorString += '</ul>'
          error.response.data.code = error.response.status
        }
        // let message = JSON.stringify(error.response.data)
        let message = errorString
        if ('errors' in error.response.data) {
          message = JSON.stringify(error.response.data.errors)
        }
        if ('message' in error.response.data) {
          message = error.response.data.message
        }
        throw new Error(`${error.response.data.code}: ${message}`)
      } else {
        throw new Error(error)
      }
    })
    return response as T
  }

  public static getBaseUrl(): string {
    return process.env.VUE_APP_API_BASE_URL
  }

  public static getBearerToken(): string {
    return store.getters.token
  }
}
